/**
 * Date/time helpers for the todo screen — the module's own copy, so it imports
 * nothing from another module (the independence rule). Same approach as the
 * agenda's: `<input type="date/time">` speak the device's local time, and this
 * household is in one place, so a local wall time round-trips through the
 * device's own zone.
 */

const pad = (n: number): string => String(n).padStart(2, '0');

/** ISO instant → the `YYYY-MM-DD` a date input wants, in local time. */
export function isoToDateInput(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** ISO instant → the `HH:MM` a time input wants, in local time. */
export function isoToTimeInput(iso: string): string {
  const d = new Date(iso);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** A local `date` (+ optional `time`, defaulting to 09:00) → a UTC ISO instant. */
export function inputsToIso(date: string, time: string): string {
  return new Date(`${date}T${time || '09:00'}`).toISOString();
}

const whenFmt = new Intl.DateTimeFormat('nl-NL', {
  timeZone: 'Europe/Amsterdam',
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

/** "vr 3 sep 19:00" — a scheduled reminder, for the sub-line. */
export function formatWhen(iso: string): string {
  try {
    return whenFmt.format(new Date(iso));
  } catch {
    return '';
  }
}

/** True once the reminder time has passed — shown as overdue. */
export function isPast(iso: string): boolean {
  const t = Date.parse(iso);
  return Number.isFinite(t) && t < Date.now();
}

/** Whole-day offset from today in local time: 0 = today, 1 = tomorrow, -1 = yesterday. */
export function dayOffset(iso: string): number {
  const d = new Date(iso);
  const now = new Date();
  const a = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const b = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  return Math.round((a - b) / 86_400_000);
}

const dayFmt = new Intl.DateTimeFormat('nl-NL', {
  timeZone: 'Europe/Amsterdam',
  weekday: 'short',
  day: 'numeric',
  month: 'short',
});

/** "vr 3 sep" — a date without the time, for the "Volgende:" line of a waiting todo. */
export function formatDay(iso: string): string {
  try {
    return dayFmt.format(new Date(iso));
  } catch {
    return '';
  }
}

/**
 * A compact, relative due label for the row's date pill: "Vandaag 18:00",
 * "Morgen", "Gisteren 09:00", else the full "vr 3 sep 19:00". The nearest three
 * days read as words because that is how the household actually thinks about them.
 */
export function formatDue(iso: string): string {
  const off = dayOffset(iso);
  const time = isoToTimeInput(iso);
  const t = time && time !== '00:00' ? ` ${time}` : '';
  if (off === 0) return `Vandaag${t}`;
  if (off === 1) return `Morgen${t}`;
  if (off === -1) return `Gisteren${t}`;
  return formatWhen(iso);
}

/**
 * A quick-pick date as the `YYYY-MM-DD` a date input wants, in local time —
 * the scheduler's chips. "weekend" is the coming Saturday (today if it is one);
 * "nextWeek" is the coming Monday, always at least a day ahead.
 */
export function quickDate(kind: 'today' | 'tomorrow' | 'weekend' | 'nextWeek'): string {
  const d = new Date();
  d.setHours(12, 0, 0, 0); // midday, so only the Y/M/D we read is never nudged across a DST edge
  if (kind === 'tomorrow') d.setDate(d.getDate() + 1);
  else if (kind === 'weekend') d.setDate(d.getDate() + ((6 - d.getDay() + 7) % 7));
  else if (kind === 'nextWeek') d.setDate(d.getDate() + (((1 - d.getDay() + 7) % 7) || 7));
  return isoToDateInput(d.toISOString());
}

/**
 * The weekday picker's labels, ordered Monday-first for the UI but carrying the
 * 0–6 Sun–Sat value the rule stores (matching `Date.getUTCDay`).
 */
export const WEEKDAYS: { value: number; short: string }[] = [
  { value: 1, short: 'ma' },
  { value: 2, short: 'di' },
  { value: 3, short: 'wo' },
  { value: 4, short: 'do' },
  { value: 5, short: 'vr' },
  { value: 6, short: 'za' },
  { value: 0, short: 'zo' },
];

/** The Dutch verb each log action reads as in a todo's history. */
const LOG_VERBS: Record<import('@familyhub/shared').TodoLogAction, string> = {
  created: 'Aangemaakt',
  completed: 'Afgevinkt',
  reopened: 'Heropend',
  recurred: 'Opnieuw ingepland',
  edited: 'Bewerkt',
};

/** "Aangemaakt", "Bewerkt: categorie" — one history entry's what-happened text. */
export function describeLogEntry(entry: import('@familyhub/shared').TodoLogEntry): string {
  const verb = LOG_VERBS[entry.action] ?? entry.action;
  return entry.action === 'edited' && entry.detail ? `${verb}: ${entry.detail}` : verb;
}

/** "Elke dag", "Elke 3 dagen", "ma, wo, vr" — a repeat rule for the sub-line. */
export function describeRecurrence(rule: import('@familyhub/shared').TodoRecurrence): string {
  if (rule.freq === 'daily') {
    return rule.interval <= 1 ? 'Elke dag' : `Elke ${rule.interval} dagen`;
  }
  const days = WEEKDAYS.filter((d) => rule.weekdays.includes(d.value)).map((d) => d.short);
  if (days.length === 7) return 'Elke dag';
  return days.join(', ');
}
